import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams } from "react-router-dom";
import RechargeHistoryComponent from "./component";

const RechargeHistoryContainer = (props) => {
  const navigate = useNavigate();
  const { corporateId } = useParams();
  
  const [rechargeHistory, setRechargeHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalEntries, setTotalEntries] = useState(0);
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: ""
  });
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showDepartmentFilter, setShowDepartmentFilter] = useState(false);
  const tableContainerRef = useRef(null);

  // Extract unique departments from data
  useEffect(() => {
    if (rechargeHistory.length > 0) {
      const uniqueDepartments = [];
      rechargeHistory.forEach(item => {
        if (item.departmentName && !uniqueDepartments.some(dept => dept.name === item.departmentName)) {
          uniqueDepartments.push({
            id: item.departmentName,
            name: item.departmentName
          });
        }
      });
      // Add "All Departments" option
      setDepartments([
        { id: "all", name: "All Departments" },
        ...uniqueDepartments
      ]);
    }
  }, [rechargeHistory]);

  useEffect(() => {
    if (corporateId) {
      console.log("Fetching recharge history for Corporate:", corporateId);
      handleGetRechargeHistory(corporateId, 1);
    }
  }, [corporateId]);

  // Apply filters whenever dependencies change
  useEffect(() => {
    handleFilterHistory();
  }, [searchValue, rechargeHistory, dateFilter, selectedDepartment]);

  const handleGetRechargeHistory = (corpId, page = 1) => {
    if (page === 1) {
      setLoading(true);
      setRechargeHistory([]);
      setFilteredHistory([]);
    } else {
      setLoadingMore(true);
    }
    
    let url = `/partner/api/v1/${corpId}/recharge-history`;
    
    console.log("API Call URL:", url);
    
    props.getDataFromAPI(
      url,
      "GET",
      undefined,
      (response) => {
        console.log("Recharge History API Response:", response);
        
        if (!response) {
          console.error("API returned null or undefined response");
          toastr.error("Error", "No data received from server");
          setLoading(false);
          setLoadingMore(false);
          return;
        }

        let data = [];
        let total = 0;
        
        // Handle different response formats
        if (Array.isArray(response)) {
          data = response;
          total = response.length;
        } else if (response.data && Array.isArray(response.data)) {
          data = response.data;
          total = response.data.length;
        } else if (response.rechargeHistory && Array.isArray(response.rechargeHistory)) {
          data = response.rechargeHistory;
          total = response.rechargeHistory.length;
        } else {
          console.error("Unexpected API response format:", response);
          toastr.warning("Warning", "No recharge history data found");
          data = [];
          total = 0;
        }

        // Sort by rechargeTime descending (newest first)
        data.sort((a, b) => {
          return new Date(b.rechargeTime) - new Date(a.rechargeTime);
        });

        if (page === 1) {
          setRechargeHistory(data);
        } else {
          setRechargeHistory(prev => [...prev, ...data]);
        }
        
        setTotalEntries(total);
        setHasMore(data.length === 20); // Assuming 20 items per page
        setCurrentPage(page);
        setLoading(false);
        setLoadingMore(false);
      },
      (err) => {
        console.error("API Error:", err);
        toastr.error("Failed", "Unable to fetch recharge history");
        setLoading(false);
        setLoadingMore(false);
        setHasMore(false);
      }
    );
  };

  // Helper function to extract date from rechargeTime string (YYYY-MM-DD)
  const getDateFromString = (dateTimeString) => {
    if (!dateTimeString) return null;
    try {
      // Split the string by 'T' and take the first part (date part)
      // Example: "2026-03-22T18:30:02" -> "2026-03-22"
      const datePart = dateTimeString.split('T')[0];
      return datePart;
    } catch (error) {
      console.error("Date extraction error:", error);
      return null;
    }
  };

  const handleFilterHistory = () => {
    let filtered = [...rechargeHistory];
    
    // Apply search filter
    if (searchValue.trim()) {
      const searchString = searchValue.toLowerCase();
      filtered = filtered.filter((item) => {
        const ruleId = (item.ruleId || "").toString().toLowerCase();
        const department = (item.departmentName || "").toString().toLowerCase();
        const amount = (item.totalRechargeAmount || 0).toString().toLowerCase();
        
        return (
          ruleId.includes(searchString) ||
          department.includes(searchString) ||
          amount.includes(searchString)
        );
      });
    }
    
    // Apply department filter
    if (selectedDepartment !== "all") {
      filtered = filtered.filter(item => {
        if (selectedDepartment === "null" || selectedDepartment === "undefined") {
          return !item.departmentName;
        }
        return item.departmentName === selectedDepartment;
      });
    }
    
    // Apply date filter - INCLUSIVE date range
    if (dateFilter.startDate && dateFilter.endDate) {
      filtered = filtered.filter(item => {
        if (!item.rechargeTime) return false;
        
        // Extract date directly from the string without any timezone conversion
        const itemDate = getDateFromString(item.rechargeTime);
        
        if (!itemDate) return false;
        
        // INCLUSIVE comparison: include both start and end dates
        // Direct string comparison works because format is YYYY-MM-DD
        return itemDate >= dateFilter.startDate && itemDate <= dateFilter.endDate;
      });
    } 
    // Apply single date filter (if only start date selected)
    else if (dateFilter.startDate && !dateFilter.endDate) {
      filtered = filtered.filter(item => {
        if (!item.rechargeTime) return false;
        const itemDate = getDateFromString(item.rechargeTime);
        return itemDate && itemDate === dateFilter.startDate;
      });
    }
    // Apply single date filter (if only end date selected)
    else if (!dateFilter.startDate && dateFilter.endDate) {
      filtered = filtered.filter(item => {
        if (!item.rechargeTime) return false;
        const itemDate = getDateFromString(item.rechargeTime);
        return itemDate && itemDate === dateFilter.endDate;
      });
    }
    
    setFilteredHistory(filtered);
  };

  const handleSearchInputChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleSearchIconClick = () => {
    handleFilterHistory();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleFilterHistory();
    }
  };

  const handleDateFilterChange = (type, value) => {
    setDateFilter(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleDepartmentFilterChange = (deptId) => {
    setSelectedDepartment(deptId);
  };

  const handleClearFilters = () => {
    setSearchValue("");
    setDateFilter({ startDate: "", endDate: "" });
    setSelectedDepartment("all");
    setShowDateFilter(false);
    setShowDepartmentFilter(false);
    setFilteredHistory(rechargeHistory);
  };

  const handleLoadMore = () => {
    if (corporateId && hasMore) {
      handleGetRechargeHistory(corporateId, currentPage + 1);
    }
  };

  const handleRefresh = () => {
    if (corporateId) {
      setDateFilter({ startDate: "", endDate: "" });
      setSelectedDepartment("all");
      setSearchValue("");
      handleGetRechargeHistory(corporateId, 1);
    }
  };

  const handleDateFilterApply = () => {
    // Close the filter popup
    setShowDateFilter(false);
  };

  return (
    <RechargeHistoryComponent
      rechargeHistory={filteredHistory}
      loading={loading}
      loadingMore={loadingMore}
      searchValue={searchValue}
      handleSearchInputChange={handleSearchInputChange}
      handleSearchIconClick={handleSearchIconClick}
      handleKeyPress={handleKeyPress}
      handleLoadMore={handleLoadMore}
      hasMore={hasMore}
      tableContainerRef={tableContainerRef}
      corporateId={corporateId}
      totalEntries={totalEntries}
      
      // Date filter props
      dateFilter={dateFilter}
      handleDateFilterChange={handleDateFilterChange}
      showDateFilter={showDateFilter}
      setShowDateFilter={setShowDateFilter}
      onDateFilterApply={handleDateFilterApply}
      
      // Department filter props
      departments={departments}
      selectedDepartment={selectedDepartment}
      handleDepartmentFilterChange={handleDepartmentFilterChange}
      showDepartmentFilter={showDepartmentFilter}
      setShowDepartmentFilter={setShowDepartmentFilter}
      
      // Clear filters
      handleClearFilters={handleClearFilters}
      handleRefresh={handleRefresh}
    />
  );
};

function mapStateToProps(props) {
  return {
    props,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(RechargeHistoryContainer);